import type { Bundle, Patient, Provenance } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { Loader, Message, Card } from 'semantic-ui-react';
import useSWR from 'swr';
import type { PatientEntry } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FHIRFetcher from '../../../../data/FHIRFetcher';
import type { ApiFetchError } from '../../../../services/api-client';
import * as FHIRUtils from '../../../../utils/FHIRUtils';
import MemberCard from '../card';



import {
  normalizeSearchString,
  buildSearchParam,
  commonSWROptions,
  type PatientSearchResult,
} from './members-list-utils';

interface RecentMembersListProps {
  payer: string;
  searchString: string;
  selectedEligibilityGroup?: string | null;
  refresh: boolean;
  setRefresh: (value: boolean) => void;
  onPatientSelect: (patient: Patient) => void;
}

function RecentMembersList(props: RecentMembersListProps) {
  const authInfo = useAuthContext();
  // A comma-joined `_id` list, or `null` until the Provenance fetch has built it —
  // the null is what defers the patient query below.
  const [myPatientIds, setMyPatientIds] = React.useState<string | null>(null);
  const [patientGroups, setPatientGroups] = React.useState<PatientEntry[]>([]);

  const handlePatientSelect = (selectedPatient: Patient) => {
    props.onPatientSelect(selectedPatient);
  };

  const normalizedSearchString = useMemo(() => normalizeSearchString(props.searchString), [props.searchString]);

  const patientOptions = useMemo(
    () => ({
      ...commonSWROptions,
      onSuccess: (data: PatientSearchResult) => {
        const tempPatient: PatientEntry[] = [];
        //TODO: move this to fhir-proxy later
        //filter out the patient data that doesn't meet the criteria
        if (data && data?.entry?.length > 0) {
          for (let i = 0; i < data?.entry?.length; i++) {
            // `i` is always in range under the loop bound; the assertion records that
            // for `noUncheckedIndexedAccess` and applies to the ELEMENT, not to the
            // optional chain — `data?.entry[i]!` would assert away the `?.` itself.
            const patient = data?.entry[i] as PatientEntry;

            const patientEligibilityGroups = fhirpath.evaluate(
              patient,
              "resource.identifier.where(system = 'https://projectwell.io/fhir/identifiers/eligibility-group').value"
            );
            if (props?.selectedEligibilityGroup && patientEligibilityGroups?.includes(props.selectedEligibilityGroup)) {
              tempPatient.push(patient);
            } else if (!props?.selectedEligibilityGroup) {
              tempPatient.push(patient);
            }
          }
        }
        setPatientGroups(tempPatient);
      },
    }),
    [props?.selectedEligibilityGroup]
  );

  const provenanceOptions = useMemo(
    () => ({
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      onSuccess: (data: Bundle | undefined, _key: unknown, _config: unknown) => {
        if (data && data.entry && data.entry.length) {
          const patients = data.entry.map((provenance) => {
            // `parseFHIRProvenancePatients` requires a `Provenance`, but
            // `BundleEntry.resource` is `0..1` and the whole `FhirResource` union.
            // This query is `/Provenance/?...`, so every entry carries one; the
            // assertion records that rather than filtering, which would change
            // which entries reach `patients`.
            return FHIRUtils.parseFHIRProvenancePatients(provenance.resource as Provenance);
          });

          const uniquePatients = patients.filter((c, index: number) => {
            if (c && c !== '') return patients.indexOf(c) === index;
          });

          if (uniquePatients && uniquePatients.length) setMyPatientIds(uniquePatients.join(','));
        }
      },
    }),
    []
  );

  //standard patient query
  // last one week updated data
  const { patientQuery, provenanceKey } = useMemo(() => {
    const searchParam = buildSearchParam(normalizedSearchString);
    const lastWeekDate = moment().subtract(7, 'd').format('YYYY-MM-DD');

    if (props.payer === 'ALL') {
      return {
        patientQuery: `/Patient/?identifier:not=INACTIVE${searchParam}`,
        provenanceKey: `/Provenance/?_elements=target&_count=1000&_sort=-_lastUpdated&_lastUpdated=gt${lastWeekDate}${normalizedSearchString ? `&_content=${encodeURIComponent(normalizedSearchString)}` : ''}`,
      };
    } else {
      return {
        patientQuery: `/Patient/?identifier:not=INACTIVE&organization=${props.payer}&_sort=-_lastUpdated${searchParam}`,
        provenanceKey: `/Provenance/?agent=${props.payer}&_elements=target&_count=1000&_sort=-_lastUpdated&_lastUpdated=gt${lastWeekDate}${normalizedSearchString ? `&_content=${encodeURIComponent(normalizedSearchString)}` : ''}`,
      };
    }
  }, [props.payer, normalizedSearchString]);

  const { isValidating: provenanceDataLoading } = useSWR<Bundle | undefined, ApiFetchError>(
    [provenanceKey, authInfo.token],
    FHIRFetcher,
    provenanceOptions
  );

  //Note: initially myPatientIds will be null and once the above Provenance fetch is complete that myPatientIds list is built
  const {
    data: _patientData,
    error: patientError,
    isValidating: patientsDataLoading,
    mutate: patientsDataMutate,
  } = useSWR<PatientSearchResult, ApiFetchError>(
    [myPatientIds ? patientQuery + '&_id=' + myPatientIds : null, authInfo.token],
    FHIRFetcher,
    patientOptions
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
    patientsDataMutate();
  }, [props?.selectedEligibilityGroup, patientsDataMutate]);

  useEffect(() => {
    if (props.refresh === true) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
      patientsDataMutate();
      props.setRefresh(false);
    }
  }, [props.refresh, patientsDataMutate, props.setRefresh]);

  const isLoading = provenanceDataLoading || patientsDataLoading;

  return (
    <div>
      <p />

      {isLoading && <Loader active inline="centered" content="Loading members..." />}

      {patientGroups && patientGroups.length < 1 && !isLoading && (
        <Message warning header="No members found!" content="Please refine your search." />
      )}

      {!isLoading && patientGroups && patientGroups.length > 0 && (
        <Card.Group>
          {patientGroups.map((patientEntry) => {
            return (
              <MemberCard
                key={patientEntry.resource.id}
                patient={patientEntry.resource}
                onPatientSelect={handlePatientSelect}
              />
            );
          })}
        </Card.Group>
      )}

      {patientError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(patientError as { message?: string }).message}</p>
        </Message>
      )}
    </div>
  );
}

export default RecentMembersList;
